import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import normalizeEnums from "@/utils/normalize_enums";
import getPharmacyID from "@/utils/pharmacy_code";
import {
  DialogOverlay,
  DialogTrigger,
} from "@radix-ui/react-dialog";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { PopoverAnchor } from "@radix-ui/react-popover";
import {
  useNavigate,
  useRouterState
} from "@tanstack/react-router";
import { CommandItem, CommandList } from "cmdk";
import { ChevronLeft, LogOut, Play, X } from "lucide-react";
import React, { useEffect, useState } from "react";
// import { httpClient } from "@/httpClient";

function getTabName(tab: string) {
  const path = tab.split("/")[0];
  switch (path) {
    case "":
      return "overview"
    case "invoicing":
      return "reports"
    case "create-purchase-invoice":
      return "inventory"
    case "create-purchase-order":
      return "inventory"
    case "draft-purchase-invoice":
      return "inventory"
    default:
      return path
  }
}

enum OS {
  Windows = "Windows",
  Macintosh = "Macintosh",
  Linux = "Linux",
}

const NavLink = ({ to, children, className, isHome }: { to: string, children: React.ReactNode, className?: string, isHome: boolean }) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    if (getPharmacyID() === "") {
      e.preventDefault();
      return;
    }
    if (!isHome) {
      navigate({ to });
    }
  };

  console.log(getPharmacyID())

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`${className} ${getPharmacyID() === "" ? 'opacity-50 cursor-not-allowed' : (isHome ? 'cursor-not-allowed' : 'cursor-pointer')}`}
            onClick={handleClick}
          >
            {children}
          </div>
        </TooltipTrigger>
        {getPharmacyID() === "" && (
          <TooltipContent>
            <p>Please select a pharmacy first</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

const Index = () => {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const navigate = useNavigate();
  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);
  const [os, setOs] = useState(OS.Windows);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [pharmacies, setPharmacies] = useState<Array<{ pharmacy_code: string; pharmacy_name: string }>>([
    { pharmacy_code: "GIMSR", pharmacy_name: "GIMSR" },
  ]);
  const [selectedCode, setSelectedCode] = useState<string>("GIMSR");
  // const API_URL = import.meta.env.VITE_API_URL;

  const placeholdres = [
    "Search for medicines",
    "Search for invoices",
    "Search for reports",
  ];
  const [placeHolderIdx, setPlaceHolderIdx] = useState(0);
  const [placeholder, setPlaceholder] = useState(placeholdres[placeHolderIdx]);

  useEffect(() => {
    const ua = window.navigator.userAgent;
    if (ua.includes("Macintosh")) {
      setOs(OS.Macintosh);
    }
    const placeholderRotatior = setInterval(() => {
      //weird issues with useState closures and use effect
      //https://stackoverflow.com/questions/53024496/state-not-updating-when-using-react-state-hook-within-setinterval
      setPlaceHolderIdx((oldIdx) => (oldIdx + 1) % placeholdres.length);
    }, 2500);
    return () => {
      clearInterval(placeholderRotatior);
    };
  }, []);

  // useEffect(() => {
  //   const fetchPharmacies = async () => {
  //     try {
  //       const res = await httpClient().get(`${API_URL}/public/pharmacy/list`);
  //       const list = res.data?.data as Array<{ pharmacy_code: string; pharmacy_name: string }>;
  //       setPharmacies(list || []);
  //     } catch (e) {
  //       setPharmacies([]);
  //     }
  //   };
  //   fetchPharmacies();
  // }, [API_URL]);

  const navigationItems = [
    { name: "Overview", path: "/overview" },
    { name: "Sales", path: "/sales" },
    { name: "Inventory", path: "/inventory" },
    { name: "Reports", path: "/invoicing" },
    { name: "Suppliers", path: "/suppliers" },
    { name: "Patients", path: "/patients" },
    { name: "Accounts", path: "/accounts" },
    { name: "Insurance", path: "/insurance", disabled: true },
  ];

  const isHome = currentPath === "/home";

  const {toast} = useToast();

  // Set currentTab to undefined if isHome, otherwise determine the tab
  const currentTab = isHome
    ? undefined
    : navigationItems.find(item => currentPath.startsWith(item.path))?.name.toLowerCase();

  return (
    <nav className="sticky top-0 flex border-b-[1px] bg-background p-4 z-50 gap-4 justify-between print:hidden">
      <div className="flex items-center justify-center space-x-2">
        <div className="border rounded-md h-8 px-2 flex items-center gap-2">
          <div className="grid place-items-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 82 82"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.6852 36.5542L21.046 0H27.0024L21.6416 36.5542H15.6852ZM28.7893 36.5542L34.1501 0H40.1065L34.7458 36.5542H28.7893ZM52.0194 7.70602L53.2107 0H59.1671L57.9758 7.70602H52.0194ZM65.1235 7.70602L66.3148 0H72.2712L71.0799 7.70602H65.1235ZM5.95642 18.5735V12.6458H13.8983L13.1041 18.5735H5.95642ZM42.4891 18.5735L43.2833 12.6458H82V18.5735H42.4891ZM5.95642 31.6145V25.6867H12.1114L11.1186 31.6145H5.95642ZM40.5036 31.6145L41.4964 25.6867H82V31.6145H40.5036ZM54.2034 82L60.954 36.5542H66.9104L60.1598 82H54.2034ZM41.0993 82L47.8499 36.5542H53.8063L47.0557 82H41.0993ZM0 50.188V44.2602H41.4964L40.5036 50.188H0ZM69.8886 50.188L70.8814 44.2602H76.0436V50.188H69.8886ZM0 63.2289V57.3012H39.5109L38.7167 63.2289H0ZM67.9031 63.2289L68.8959 57.3012H76.0436V63.2289H67.9031ZM8.93463 82L10.9201 68.1687H16.8765L14.891 82H8.93463ZM22.0387 82L24.0242 68.1687H29.9806L27.9952 82H22.0387Z"
                fill="#787878"
              />
            </svg>
          </div>
          <div className="h-4 w-[1px] bg-border" />
          <Select value={selectedCode || undefined} onValueChange={(val) => {
            setSelectedCode(val);
            try { localStorage.setItem('pharmacy_id', val); } catch {}
          }}>
            <SelectTrigger className="h-8 w-[140px] border-0 rounded-none bg-transparent px-1 text-xs focus:ring-0 focus:ring-offset-0 focus:outline-none outline-none">
              <SelectValue placeholder="Select Branch" />
            </SelectTrigger>
            <SelectContent>
              {pharmacies.map((p) => (
                <SelectItem key={p.pharmacy_code} value={p.pharmacy_code}>
                  {p.pharmacy_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-600 text-white flex items-center"
          onClick={() => toast({
            title: "Not available yet",
            description: "This feature is coming soon. Stay tuned!",
          }) }
        >
          Start Now
          <span className="ml-2 h-5 w-5 rounded-full bg-white flex items-center justify-center">
            <Play size={12} className="text-blue-600" />
          </span>
        </Button>
        <div
          className={`block md:hidden w-[150px]   ${mobileNavMenuOpen ? "rounded-t-md border border-b-white" : "rounded-md border"}`}
        >
          <Popover open={mobileNavMenuOpen} onOpenChange={setMobileNavMenuOpen}>
            <Dialog
              open={mobileNavMenuOpen}
              onOpenChange={setMobileNavMenuOpen}
            >
              <PopoverTrigger asChild>
                <DialogTrigger asChild>
                  <button
                    role="combobox"
                    className="w-full flex flex-row justify-between items-center px-2 py-2 border-none"
                  >
                    {normalizeEnums(getTabName(currentTab || ""))}
                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </DialogTrigger>
              </PopoverTrigger>
              <PopoverAnchor />
              <DialogOverlay></DialogOverlay>
              <PopoverContent className="border border-t-0 rounded-b-lg w-[150px]">
                <DialogContent asChild>
                  <Command>
                    <CommandList className="w-[150px] border-0">
                      <CommandGroup className="flex flex-col gap-2 px-2 text-normal ">
                        <CommandSeparator />
                        {navigationItems.map((item, index) => (
                          <React.Fragment key={index}>
                            <CommandItem asChild>
                              <NavLink
                                to={item.path}
                                className={`py-2 ${(isHome || item.disabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                isHome={isHome}
                              >
                                {item.name}
                              </NavLink>
                            </CommandItem>
                            <CommandSeparator />
                          </React.Fragment>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </DialogContent>
              </PopoverContent>
            </Dialog>
          </Popover>
        </div>
        
      </div>
      <div className="flex items-center justify-center space-x-2">
        <div
          role="button"
          onMouseDown={(e) => {
            if (getPharmacyID() === "") {
              e.preventDefault();
              return;
            }
            // const commandEvent = new Event("open-command-menu");
            // document.dispatchEvent(commandEvent);
            toast({
              title: "Not available yet",
              description: "This feature is coming soon. Stay tuned!",
            })
          }}
          className={`md:flex hidden h-8 items-center gap-2 rounded-md px-2 border border-border bg-white shadow-sm ${getPharmacyID() === "" ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span className="text-xs">Quick actions</span>
          <span className="h-5 px-2 rounded-md border text-[10px] font-semibold text-muted-foreground flex items-center gap-1">
            {os === OS.Macintosh ? (
              <>
                <kbd>⌘</kbd>
                <kbd>K</kbd>
              </>
            ) : (
              <>
                <kbd>Ctrl</kbd>
                <kbd>K</kbd>
              </>
            )}
          </span>
        </div>
        
        <Sheet open={showShortcuts} onOpenChange={setShowShortcuts}>
          <SheetContent side="right" className="max-w-md w-full overflow-scroll">
            <div className="flex items-center justify-between pb-2">
              <div className="flex items-center gap-1">

              <button
                
                aria-label="Back"
                onClick={() => setShowShortcuts(false)}
                >
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </button>
              <p className="flex-1 text-center text-lg font-semibold">
                Keyboard Shortcuts
              </p>
                </div>
              <button
                className="p-2"
                aria-label="Close"
                onClick={() => setShowShortcuts(false)}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <Input
              placeholder="Search shortcuts"
              className="mb-4"
            />
            <div className="space-y-6">
              <div>
                <div className="font-semibold text-sm mb-2">General</div>
                <ul className="space-y-2 text-sm">
                  <ShortcutRow label="Open command menu" keys={os === OS.Macintosh ? ["⌘", "K"] : ["Ctrl", "K"]} />
                  <ShortcutRow label="View keyboard shortcuts" keys={os === OS.Macintosh ? ["⌘", "/"] : ["Ctrl", "/"]} />
                </ul>
              </div>
              <div>
                <div className="font-semibold text-sm mb-2">Sales</div>
                <ul className="space-y-2 text-sm">
                  <ShortcutRow label="Move selection up" keys={["↑"]} />
                  <ShortcutRow label="Move selection down" keys={["↓"]} />
                  <ShortcutRow label="Cycle active sales" keys={["S"]} />
                  <ShortcutRow label="Focus product search" keys={["Space"]} />
                  <ShortcutRow label="Checkout" keys={["Shift", "Enter"]} />
                  <ShortcutRow label="Add new multi-sale" keys={["Shift", "S"]} />
                  <ShortcutRow label="Discard sale" keys={["Shift", "D"]} />
                  <ShortcutRow label="Increase quantity" keys={["+", "→"]} />
                  <ShortcutRow label="Decrease quantity" keys={["-", "←"]} />
                  <ShortcutRow label="Remove selected item" keys={["Shift", "Backspace"]} />
                </ul>
              </div>
            </div>
           
          </SheetContent>
        </Sheet>
        {(
          <button
            className="h-8 w-8 rounded-md grid place-items-center cursor-default border border-border"
            onClick={() =>{
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              navigate({ to: '/login' });
            }}
          >
            <LogOut size={16} />
          </button>
        )}
      </div>
    </nav>
  );
};

export default Index;

// Helper component for shortcut row
function ShortcutRow({ label, keys }: { label: string; keys: string[] }) {
  return (
    <li className="flex items-center justify-between">
      <span>{label}</span>
      <span className="flex gap-1">
        {keys.map((key, idx) => (
          <kbd key={idx} className="px-2 py-1 border rounded bg-muted text-xs font-mono">{key}</kbd>
        ))}
      </span>
    </li>
  );
}
